import React, {
  useCallback, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Table } from 'antd';
import PropTypes from 'prop-types';

import CompanySettingsCard from '../CompanySettingsCard';
import CompanyEditRow from '../CompanyEditRow';
import ConfirmPasswordModal from '../ConfirmPasswordModal';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import DisplayText from '../../common/text/DisplayText';
import Colors from '../../constants/Colors';
import sortByString, { safeExtractFromObject } from '../../helpers/helpers';
import { resubscribeEmail } from '../../emailSubscriptions/state/emailSubscription.actions';
import { DateTime } from 'luxon';

const formatTimestamp = (timestamp) => {
  if (!timestamp) return null;
  return DateTime.fromISO(timestamp).toLocaleString(DateTime.DATE_MED);
};

const styles = {
  emptyText: {
    fontFamily: 'roboto-regular',
    color: Colors.ONTRACCR_OPACITY_GRAY,
    margin: '8px 10px 16px',
  },
  resubscribeButton: {
    width: 'auto',
    minWidth: 110,
    height: 32,
    paddingLeft: 12,
    paddingRight: 12,
  },
};

function UnsubscribedEmailsCard({ rowStyle }) {
  const dispatch = useDispatch();
  const settings = useSelector((state) => safeExtractFromObject(state, 'settings.company.settings'));
  const unsubscribedEmailMap = useSelector((state) => state.unsubscribedEmails);
  const [pendingResubscribeId, setPendingResubscribeId] = useState(null);

  const unsubscribedEmails = useMemo(() => (
    Object.values(unsubscribedEmailMap).sort(sortByString('email'))
  ), [unsubscribedEmailMap]);

  const closePasswordModal = useCallback(() => {
    setPendingResubscribeId(null);
  }, []);

  const handlePasswordConfirm = useCallback(async (password) => {
    if (!pendingResubscribeId) return false;
    return dispatch(resubscribeEmail(pendingResubscribeId, password));
  }, [pendingResubscribeId]);

  const columns = useMemo(() => [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Unsubscribed At',
      dataIndex: 'unsubscribedAt',
      key: 'unsubscribedAt',
      render: (text) => formatTimestamp(text),
    },
    {
      title: '',
      key: 'action',
      width: 130,
      align: 'right',
      render: (_, record) => (
        <OnTraccrButton
          title="Resubscribe"
          type="secondary"
          style={styles.resubscribeButton}
          disabled={pendingResubscribeId != null}
          onClick={() => setPendingResubscribeId(record.id)}
        />
      ),
    },
  ], [pendingResubscribeId]);

  const onHeaderRow = useCallback(() => {
    if (!settings?.uxRedesignPt1) return null;
    return { className: 'ontraccr-table-header' };
  }, [settings]);

  return (
    <Row style={rowStyle}>
      <ConfirmPasswordModal
        visible={pendingResubscribeId != null}
        onClose={closePasswordModal}
        onConfirm={handlePasswordConfirm}
      />
      <CompanySettingsCard title="Unsubscribed Email Recipients">
        <CompanyEditRow
          title="Blocked from email"
          helpText="These recipients used the unsubscribe link in an Ontraccr email. Resubscribe them to allow notifications and workflow emails to be sent again."
          divider={unsubscribedEmails.length > 0}
        />
        {unsubscribedEmails.length === 0 ? (
          <DisplayText
            title="No unsubscribed recipients"
            style={styles.emptyText}
          />
        ) : (
          <Table
            size="small"
            pagination={false}
            columns={columns}
            dataSource={unsubscribedEmails}
            rowKey="id"
            onHeaderRow={onHeaderRow}
          />
        )}
      </CompanySettingsCard>
    </Row>
  );
}

UnsubscribedEmailsCard.propTypes = {
  rowStyle: PropTypes.shape({}),
};

UnsubscribedEmailsCard.defaultProps = {
  rowStyle: { width: '100%', marginLeft: 180 },
};

export default UnsubscribedEmailsCard;
